import { cn } from '@app/lib/cn'
import type { ChequeCurrencyTotal, ChequeLadderBucket } from './api'
import {
  currencySymbol,
  formatCompactAmount,
  formatMonth,
  formatWholeAmount,
} from './format'

/**
 * The maturity ladder — the screen's centrepiece: one rail per currency from
 * the current month to the last vade on the books, a bar per month.
 *
 * Design decisions, stated so they survive review:
 *
 *   - Empty months still render (a baseline tick). On a payment calendar a
 *     quiet month IS information; compressing the rail to non-empty buckets
 *     would hide it.
 *   - The past-vade block is a labelled chip at the rail's head, NOT a bar:
 *     it is not a month, and at 12.8M TL it would flatten every real bar on
 *     the shared scale. Hardal (warn) with its label, never colour alone.
 *   - Bars are celik, uniform: one series, one hue, magnitude only. Kordovan
 *     is reserved for primary actions app-wide (same rule as ReceiptBar).
 *   - Currencies get separate rails and separate scales — 10,000 USD on the
 *     TL scale would be a lie in both directions.
 *   - The cheque table below is the accessible/data view of the same numbers;
 *     the rail is aria-hidden decoration over it.
 */

const BAR_MAX_PX = 72
const BAR_MIN_PX = 6

/** 'YYYY-MM' sequence, inclusive; hard cap keeps a bad vade from exploding it. */
function monthRange(start: string, end: string, cap = 30): string[] {
  const startYear = Number(start.slice(0, 4))
  const startMonth = Number(start.slice(5, 7))
  const months: string[] = []
  for (let i = 0; i < cap; i += 1) {
    const total = startMonth - 1 + i
    const year = startYear + Math.floor(total / 12)
    const month = (total % 12) + 1
    const key = `${year}-${String(month).padStart(2, '0')}`
    months.push(key)
    if (key === end) break
  }
  return months
}

function CurrencyRail({
  currency,
  buckets,
  overdue,
  asOfDate,
}: {
  currency: string
  buckets: readonly ChequeLadderBucket[]
  overdue: ChequeCurrencyTotal | undefined
  /** The API's Istanbul date — the rail never consults the browser clock. */
  asOfDate: string
}) {
  const currentMonth = asOfDate.slice(0, 7)
  const lastMonth = buckets.at(-1)?.month
  const months = lastMonth && lastMonth >= currentMonth ? monthRange(currentMonth, lastMonth) : []
  const byMonth = new Map(buckets.map((bucket) => [bucket.month, bucket]))
  const maxAmount = Math.max(...buckets.map((bucket) => bucket.amount), 1)

  // The cap in monthRange is a runaway guard (a mistyped 2099 vade must not
  // render 900 columns) — anything it cuts off is surfaced, never dropped.
  const railEnd = months.at(-1) ?? currentMonth
  const beyond = buckets.filter((bucket) => bucket.month > railEnd)
  const beyondCount = beyond.reduce((sum, bucket) => sum + bucket.count, 0)
  const beyondAmount = beyond.reduce((sum, bucket) => sum + bucket.amount, 0)

  if (months.length === 0 && !overdue) return null

  return (
    <div className="flex items-end gap-3">
      <span className="eyebrow w-8 shrink-0 pb-0.5">{currencySymbol(currency) || currency}</span>

      {overdue ? (
        <div
          className="shrink-0 self-end rounded-xs border border-hardal/40 bg-hardal/10 px-2 py-1.5 text-center"
          title="Vade tarihi geçti; Korgun'da kapanış kaydı tutulmuyor"
        >
          <p className="font-mono text-data font-semibold tnum text-hardal">
            {formatCompactAmount(overdue.amount, currency)}
          </p>
          <p className="mt-0.5 whitespace-nowrap text-micro text-hardal/90">
            Vadesi geçmiş · {overdue.count} adet
          </p>
        </div>
      ) : null}

      {months.length > 0 ? (
        <div aria-hidden className="flex min-w-0 flex-1 items-end gap-1.5 overflow-x-auto pb-0.5">
          {months.map((month) => {
            const bucket = byMonth.get(month)
            const amount = bucket?.amount ?? 0
            const height =
              amount > 0 ? BAR_MIN_PX + Math.round((amount / maxAmount) * BAR_MAX_PX) : 2
            return (
              <div
                key={month}
                className="flex min-w-9 flex-1 flex-col items-center justify-end gap-1"
                title={
                  bucket
                    ? `${formatMonth(month)} — ${bucket.count} adet — ${formatWholeAmount(amount, currency)}`
                    : `${formatMonth(month)} — çek yok`
                }
              >
                <span
                  className={cn(
                    'font-mono text-micro tnum',
                    amount > 0 ? 'text-ink-muted' : 'text-transparent',
                  )}
                >
                  {amount > 0 ? formatCompactAmount(amount) : '·'}
                </span>
                <div
                  className={cn('w-full rounded-t-xs', amount > 0 ? 'bg-celik' : 'bg-hairline')}
                  style={{ height: `${height}px` }}
                />
                <span
                  className={cn(
                    'whitespace-nowrap text-micro',
                    month === currentMonth ? 'font-semibold text-ink' : 'text-ink-muted',
                  )}
                >
                  {formatMonth(month)}
                </span>
              </div>
            )
          })}
        </div>
      ) : null}

      {beyond.length > 0 ? (
        <div
          className="shrink-0 self-end rounded-xs border border-hairline bg-surface px-2 py-1.5 text-center"
          title={`Takvim ${formatMonth(railEnd)} sonrasını tek blokta gösterir`}
        >
          <p className="font-mono text-data font-semibold tnum text-ink">
            {formatCompactAmount(beyondAmount, currency)}
          </p>
          <p className="mt-0.5 whitespace-nowrap text-micro text-ink-muted">
            Sonrası · {beyondCount} adet
          </p>
        </div>
      ) : null}
    </div>
  )
}

export function MaturityLadder({
  ladder,
  overdue,
  asOfDate,
}: {
  ladder: readonly ChequeLadderBucket[]
  overdue: readonly ChequeCurrencyTotal[]
  asOfDate: string
}) {
  // TL first (the book), then the rest alphabetically (US senets today).
  const currencies = [...new Set([...ladder, ...overdue].map((item) => item.currency))].sort(
    (a, b) => (a === 'TL' ? -1 : b === 'TL' ? 1 : a.localeCompare(b)),
  )

  if (currencies.length === 0) return null

  return (
    <div className="flex flex-col gap-5">
      {currencies.map((currency) => (
        <CurrencyRail
          key={currency}
          currency={currency}
          buckets={ladder.filter((bucket) => bucket.currency === currency)}
          overdue={overdue.find((total) => total.currency === currency)}
          asOfDate={asOfDate}
        />
      ))}
    </div>
  )
}
